import React from "react"
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  styled,
  TextField,
  Typography,
} from "@mui/material"
import {
  buildUserDisplayName,
  formatDateForInput,
  getAccessStatusCode,
} from "../../../utils/adminAccessUserHelpers"
import { namesTypesTraduc } from "../../../utils/traducTypes"

const InputButton = styled(Button)(() => ({
  background: "#1C846F",
  "&:hover": {
    background: "#0CABA8",
  },
  borderRadius: "5rem",
  color: "#fff",
}))

export const AdminAccessUserConfigForm = ({
  organization,
  user,
  userFinishDate,
  roles,
  grantedRoleCodes,
  selectedRoleCodes,
  loadingRoles,
  loadingUserContext,
  enablingUserAccess,
  submittingUserRoles,
  revokingUserRole,
  error,
  successMessage,
  hasUserBaseAccess,
  onUserFinishDateChange,
  onToggleRole,
  onEnableUserAccess,
  onGrantRoles,
  onRevokeRole,
  userAccess,
  applicationInstanceId,
  appicationModules,
  states,
}) => {
  const isBusy =
    loadingUserContext ||
    enablingUserAccess ||
    submittingUserRoles ||
    revokingUserRole

  return (
    <div style={{ width: "80%" }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Datos del usuario
      </Typography>

      <Typography variant="body2" sx={{ mb: 2 }}>
        <strong>Organización:</strong>{" "}
        {organization?.organizationLegalName ||
          organization?.organizationName ||
          "-"}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        <strong>CUIT:</strong> {organization?.organizationCuit || "-"}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        <strong>Usuario:</strong> {buildUserDisplayName(user)} (
        {user?.account?.email || "-"})
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        <strong>Tipo usuario:</strong>{" "}
        {namesTypesTraduc.find(
          (option) =>
            option.code ===
            user?.type?.organizationUserTypeCode,
        )?.name || "-"}

      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        <strong>Estado usuario org:</strong>{" "}
        {states.find(
          (option) =>
            option.userStatusCode ===
            user?.status?.userStatusCode,
        )?.userStatusName || "-"}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        <strong>Application Instance ID:</strong> {applicationInstanceId || "-"}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        <strong>Estado acceso usuario:</strong>{" "}
        {getAccessStatusCode(userAccess) || "No tiene acceso"}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        <strong>Inicio del acceso:</strong>{" "}
        {formatDateForInput(
          userAccess?.accessStartDate || userAccess?.startDate,
        ) || "-"}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        <strong>Fin del acceso:</strong>{" "}
        {formatDateForInput(
          userAccess?.accessFinishDate || userAccess?.finishDate,
        ) || "-"}
      </Typography>

      {!hasUserBaseAccess ? (
        <>
          <Alert severity="info" sx={{ mt: 3, width: "50%" }}>
            El usuario no tiene acceso base activo a la aplicación. Desde aquí puedes
            darle acceso.
          </Alert>
          <Typography sx={{ mt: 2 }}>
            Ingrese hasta que fecha el usuario tendrá acceso a la aplicación:
          </Typography>
          <Box sx={{ mt: 3 }}>
            <TextField
              sx={{ width: "50%" }}
              label="Fecha de expiración"
              type="date"
              value={userFinishDate}
              onChange={(event) => onUserFinishDateChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={isBusy}
            />
          </Box>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
              width: "50%",
            }}
          >
            <InputButton onClick={onEnableUserAccess}
              disabled={isBusy || !userFinishDate}>
              {submittingUserRoles ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Confirmar alta usuario / aplicación"
              )}
            </InputButton>

          </Box>
        </>
      ) : (
        <>
          <Alert severity="success" sx={{ mt: 3 }}>
            El usuario ya tiene acceso base activo a la aplicación. Puedes administrar
            roles y dejar preparados los sectores para módulos y parámetros.
          </Alert>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            Roles de la aplicación
          </Typography>

          {loadingRoles ? <CircularProgress size={24} /> : null}

          {!loadingRoles && !roles?.length ? (
            <Alert severity="warning">
              No hay roles para mostrar o todavía falta integrar el endpoint de
              roles.
            </Alert>
          ) : null}

          {!loadingRoles && roles?.length ? (
            <FormGroup>
              {roles.map((role) => {
                const roleCode = role?.appRoleCode
                const alreadyGranted = grantedRoleCodes.includes(roleCode)

                return (
                  <Box
                    key={roleCode}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      py: 1,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            alreadyGranted ||
                            selectedRoleCodes.includes(roleCode)
                          }
                          onChange={() => onToggleRole(roleCode)}
                          disabled={alreadyGranted || isBusy}
                        />
                      }
                      label={role?.appRoleDesc || role?.appRoleName || roleCode}
                    />

                    {alreadyGranted ? (
                      <Button
                        variant="text"
                        color="error"
                        size="small"
                        onClick={() => onRevokeRole(roleCode)}
                        disabled={isBusy}
                      >
                        Revocar
                      </Button>
                    ) : null}
                  </Box>
                )
              })}
            </FormGroup>
          ) : null}

          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <InputButton onClick={onGrantRoles}
              disabled={isBusy || !selectedRoleCodes.length}>
              {submittingUserRoles ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Confirmar asignación de roles"
              )}
            </InputButton>

          </Box>

        </>
      )}
    </div>
  )
}

export default AdminAccessUserConfigForm
